import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelBody, PanelHeader } from "../../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const CreateBranch = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentComID = profileData?.parentComID ?? profileData?.ParentComID;
  const profileTimeZone = profileData?.compTimeZone ?? "";
  const profileUTCTimeZone = profileData?.compUTCTimeZone ?? "";
  const profileCompName = profileData?.compName?.trim() || "Parent Company";
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [timeZones, setTimeZones] = useState([]);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);

  const [formData, setFormData] = useState({
    compName: "",
    compDetail: "",
    compEmail: "",
    compPhone: "",
    compFax: "",
    compIndustryType: "",
    compCurrency: "",
    compCountry: "",
    compCity: "",
    compAddress: "",
    compSecCode: "",
    compType: "",
    compRemarks: "",
    compPNotificationLimit_PerDay: 10,
    compLanguage: "en",
    compWebsite: "",
    compTimeZone: "",
    compUTCTimeZone: "",
    compPublish: true,
    sourceBranchID: null,
    copyProducts: false,
  });

  useEffect(() => {
    if (parentComID == null) return;
    if (parentComID !== 0) {
      navigate("/branches");
      return;
    }

    fetchCurrencies();
    fetchBranches();
    fetchTimeZones();

    // Inherit timezone from parent company if available
    if (profileTimeZone) {
      setFormData((prev) => ({
        ...prev,
        compTimeZone: profileTimeZone,
        compUTCTimeZone: profileUTCTimeZone,
      }));
    }
  }, [parentComID, profileTimeZone, profileUTCTimeZone, navigate]);

  const fetchCurrencies = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);
      if (response.data && response.data.data) {
        setCurrencies(
          response.data.data.map((c) => ({ value: c.value, label: c.text }))
        );
      }
    } catch (err) {
      showErrorToast("Failed to load currencies");
    }
  };

  const fetchBranches = async () => {
    const parentOption = { value: CompID, label: profileCompName };
    try {
      const response = await axiosInstance.get(APIs.GET_BRANCHES, {
        params: { includeParent: true },
      });
      if (response.data && response.data.status && response.data.data) {
        // Backend returns parent first when includeParent=true, then branches
        const options = response.data.data.map((b) => ({
          value: b.compID,
          label: b.compName,
        }));
        setBranches(options);
      } else {
        setBranches([parentOption]);
      }
    } catch (err) {
      // Fallback: at least show parent company so user can copy from parent
      setBranches([parentOption]);
    }
  };

  const fetchTimeZones = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_TIMEZONE);
      if (response.data && response.data.data) {
        setTimeZones(response.data.data);
      }
    } catch (err) {
      showErrorToast("Failed to load timezones");
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSelectChange = (selectedOption, field) => {
    if (field === "compTimeZone") {
      setFormData({ 
        ...formData, 
        compTimeZone: selectedOption?.value || "",
        compUTCTimeZone: selectedOption?.label || ""
      });
    } else {
      setFormData({ ...formData, [field]: selectedOption?.value || "" });
    }
  };

  const handleCountryCityChange = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const validateForm = () => {
    if (!formData.compName?.trim()) {
      showErrorToast("Branch name is required");
      return false;
    }
    if (!formData.compEmail?.trim()) {
      showErrorToast("Email is required");
      return false;
    }
    if (!formData.compEmail.includes("@")) {
      showErrorToast("Please enter a valid email address");
      return false;
    }
    if (!formData.compSecCode?.trim()) {
      showErrorToast("Binding code is required");
      return false;
    }
    if (!formData.compIndustryType) {
      showErrorToast("Industry type is required");
      return false;
    }
    if (!formData.compCurrency) {
      showErrorToast("Currency is required");
      return false;
    }
    if (!formData.compTimeZone?.trim()) {
      showErrorToast("Time zone is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        CompName: formData.compName,
        CompDetail: formData.compDetail || "",
        CompEmail: formData.compEmail,
        CompPhone: formData.compPhone || "",
        CompFax: formData.compFax || "",
        CompIndustryType: formData.compIndustryType,
        CompCurrency: formData.compCurrency,
        CompCountry: formData.compCountry || "",
        CompCity: formData.compCity || "",
        CompAddress: formData.compAddress || "",
        CompSecCode: formData.compSecCode,
        CompType: profileData?.compType || profileData?.CompType || "",
        CompRemarks: formData.compRemarks || "",
        CompPNotificationLimit_PerDay: formData.compPNotificationLimit_PerDay || 10,
        CompLanguage: formData.compLanguage || "en",
        CompWebsite: formData.compWebsite || "",
        CompTimeZone: formData.compTimeZone || profileData?.compTimeZone || "",
        CompUTCTimeZone: formData.compUTCTimeZone || profileData?.compUTCTimeZone || "",
        CompPublish: formData.compPublish !== undefined ? formData.compPublish : true,
        SourceBranchID: formData.copyProducts && formData.sourceBranchID ? formData.sourceBranchID : null,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_BRANCH, payload);

      if (response.data && response.data.status) {
        setCreatedCredentials({
          password: response.data.data.adminPassword,
          passcode: response.data.data.adminPasscode,
          username: response.data.data.adminUserName,
          email: response.data.data.adminEmail,
        });
        setShowCredentialsModal(true);
        showSuccessToast("Branch created successfully!");
      } else {
        showErrorToast(response.data?.message || "Failed to create branch");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to create branch");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCredentialsModal = () => {
    setShowCredentialsModal(false);
    navigate("/branches");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessToast("Copied to clipboard!");
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/setting">Settings</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/branches">Branch Management</Link>
          </li>
          <li className="breadcrumb-item active">Create New Branch</li>
        </ol>
        <h1 className="page-header mb-0">Create New Branch</h1>
      </div>
      <div className="ms-auto d-flex gap-2">
        <button
          onClick={handleSubmit}
          className="btn btn-rounded px-4 rounded-pill"
          disabled={loading}
          style={{
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }
          }}
        >
          {loading ? <Preloader /> : "Create Branch"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}

        {/* General Information Panel */}
        <Panel className="mb-4">
          <PanelHeader
            style={{
              backgroundColor: themeColor,
              color: 'white',
              border: 'none',
              borderRadius: '10px 10px 0 0',
              padding: '15px 20px',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-info-circle me-2"></i>
            General Information
          </PanelHeader>
          <PanelBody
            style={{
              padding: '25px',
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              borderRadius: '0 0 10px 10px',
              border: `1px solid ${themeColor}20`
            }}
          >
            <div className="row g-4">
              <div className="col-md-6">
                <FormInput
                  label="Branch Name"
                  name="compName"
                  value={formData.compName}
                  onchange={handleInputChange("compName")}
                  required
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Email"
                  name="compEmail"
                  type="email"
                  value={formData.compEmail}
                  onchange={handleInputChange("compEmail")}
                  required
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Phone Number"
                  name="compPhone"
                  value={formData.compPhone}
                  onchange={handleInputChange("compPhone")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Fax Number"
                  name="compFax"
                  value={formData.compFax}
                  onchange={handleInputChange("compFax")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Industry Type <span className="text-danger">*</span>
                </label>
                <Select
                  options={industryTypes}
                  value={industryTypes.find((i) => i.value === formData.compIndustryType)}
                  onChange={(option) => handleSelectChange(option, "compIndustryType")}
                  styles={dropdownStyles}
                  placeholder="Select Industry Type"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Currency <span className="text-danger">*</span>
                </label>
                <Select
                  options={currencies}
                  value={currencies.find((c) => c.value === formData.compCurrency)}
                  onChange={(option) => handleSelectChange(option, "compCurrency")}
                  styles={dropdownStyles}
                  placeholder="Select Currency"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-12">
                <CountryCitySelector
                  onChange={handleCountryCityChange}
                  selectedCountry={formData.compCountry}
                  selectedCity={formData.compCity}
                  countryFieldName="compCountry"
                  cityFieldName="compCity"
                  isDarkMode={isDarkMode}
                  setLoading={setLoading}
                  selectedValueIsLabel={true}
                />
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Address"
                  name="compAddress"
                  value={formData.compAddress}
                  onchange={handleInputChange("compAddress")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Time Zone <span className="text-danger">*</span>
                </label>
                <Select
                  options={timeZones.map((tz) => ({
                    value: tz.value,
                    label: tz.text,
                  }))}
                  value={timeZones
                    .filter((tz) => tz.value === formData.compTimeZone)
                    .map((tz) => ({ value: tz.value, label: tz.text }))[0] || null}
                  onChange={(option) => handleSelectChange(option, "compTimeZone")}
                  styles={dropdownStyles}
                  placeholder="Select Time Zone"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable={false}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Binding Code"
                  name="compSecCode"
                  value={formData.compSecCode}
                  onchange={handleInputChange("compSecCode")}
                  required
                />
                <small className="text-muted" style={{ color: isDarkMode ? '#b2bec3' : '#6c757d' }}>
                  Unique code for branch identification
                </small>
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Company Detail"
                  name="compDetail"
                  type="textarea"
                  rows={3}
                  value={formData.compDetail}
                  onchange={handleInputChange("compDetail")}
                />
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Remarks"
                  name="compRemarks"
                  type="textarea"
                  rows={2}
                  value={formData.compRemarks}
                  onchange={handleInputChange("compRemarks")}
                />
              </div>
              <div className="col-md-12">
                <div className="d-flex align-items-center p-3 rounded"
                  style={{
                    backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                    border: `1px solid ${themeColor}20`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ToggleSwitch
                    isChecked={formData.compPublish}
                    onToggle={(checked) => setFormData((prev) => ({ ...prev, compPublish: checked }))}
                    label="Active Branch"
                    name="compPublish"
                    themeColor={themeColor}
                  />
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>

        {/* Product Assignment Panel */}
        <Panel className="mb-4">
          <PanelHeader
            style={{
              backgroundColor: themeColor,
              color: 'white',
              border: 'none',
              borderRadius: '10px 10px 0 0',
              padding: '15px 20px',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-boxes me-2"></i>
            Product Assignment
          </PanelHeader>
          <PanelBody
            style={{
              padding: '25px',
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              borderRadius: '0 0 10px 10px',
              border: `1px solid ${themeColor}20`
            }}
          >
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="copyProducts"
                checked={formData.copyProducts}
                onChange={(e) =>
                  setFormData({ ...formData, copyProducts: e.target.checked })
                }
              />
              <label className="form-check-label" htmlFor="copyProducts">
                Copy Products from Existing Branch
              </label>
            </div>
            {formData.copyProducts && (
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Select Source Branch
                </label>
                <Select
                  options={branches}
                  value={branches.find((b) => b.value === formData.sourceBranchID)}
                  onChange={(option) =>
                    handleSelectChange(option, "sourceBranchID")
                  }
                  styles={dropdownStyles}
                  placeholder="Select Branch"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <small className="text-muted" style={{ color: isDarkMode ? '#b2bec3' : '#6c757d' }}>
                  All products assigned to selected branch will be assigned to this new branch
                </small>
              </div>
            )}
          </PanelBody>
        </Panel>

        {/* Credentials Modal */}
        <CustomModal
          title="Branch Created Successfully!"
          isOpen={showCredentialsModal}
          onClose={handleCloseCredentialsModal}
          isCentered={true}
        >
          <div style={{ padding: "20px" }}>
            <div className="alert alert-warning" style={{ 
              backgroundColor: isDarkMode ? '#856404' : '#fff3cd',
              borderColor: isDarkMode ? '#856404' : '#ffc107',
              color: isDarkMode ? '#ffffff' : '#856404'
            }}>
              <strong>Important:</strong> Please save these credentials securely. They will not be shown again.
            </div>
            <div className="mb-3">
              <label className="form-label">
                <strong>Admin Username:</strong>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={createdCredentials?.username || ""}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => copyToClipboard(createdCredentials?.username)}
                >
                  <i className="fa-solid fa-copy"></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                <strong>Admin Email:</strong>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={createdCredentials?.email || ""}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => copyToClipboard(createdCredentials?.email)}
                >
                  <i className="fa-solid fa-copy"></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                <strong>Admin Password:</strong>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={createdCredentials?.password || ""}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => copyToClipboard(createdCredentials?.password)}
                >
                  <i className="fa-solid fa-copy"></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                <strong>Admin Passcode:</strong>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={createdCredentials?.passcode || ""}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => copyToClipboard(createdCredentials?.passcode)}
                >
                  <i className="fa-solid fa-copy"></i>
                </button>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary"
                onClick={handleCloseCredentialsModal}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                I've Saved the Credentials
              </button>
            </div>
          </div>
        </CustomModal>
    </div>
  );
};

export default CreateBranch;
